import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { detectIncognito } from 'detectincognitojs';
import * as React from 'react';
import { DEFAULT_SCENE_TYPE } from '../redux/reducers/sceneReducer';
import { getDeviceDetectInfo } from './deviceDetectUtils';
import { getFpsStats } from './fpsUtils';
import { isLocalhostEnvironment } from './utils';
import { getBuildTime, getBuildTimestamp, getGitCommit } from './versionUtils';
const API_KEY = '7c29c65751b8c95519e900edf1074974';
// SINGLETON ALERT!
const BUGSNAG_SESSION_STATE = {
    sceneType: DEFAULT_SCENE_TYPE
};
function getBugsnagContext() {
    const { pathname } = new URL(window.location.href);
    const { username, sceneType } = BUGSNAG_SESSION_STATE;
    if (username) {
        return `${username} in ${sceneType} (${pathname})`;
    }
    return `${sceneType} scene (${pathname})`;
}
export function setBugsnagScene(sceneType, panelType) {
    if (isLocalhostEnvironment()) {
        return;
    }
    BUGSNAG_SESSION_STATE.sceneType = sceneType;
    BUGSNAG_SESSION_STATE.lastRoomEnterTimestampMs = performance.now();
    setBugsnagPanelType(panelType);
    Bugsnag.setContext(getBugsnagContext());
}
export function setBugsnagPanelType(panelType) {
    if (isLocalhostEnvironment()) {
        return;
    }
    BUGSNAG_SESSION_STATE.panelType = panelType;
}
export function setBugsnagUser(username, playerId) {
    if (isLocalhostEnvironment()) {
        return;
    }
    BUGSNAG_SESSION_STATE.username = username;
    const playerIdString = `${playerId}`;
    const email = undefined;
    Bugsnag.setUser(playerIdString, email, username);
    Bugsnag.setContext(getBugsnagContext());
}
export function getCurrentBugsnagSceneType() {
    return BUGSNAG_SESSION_STATE.sceneType;
}
function getBuildInfo() {
    return {
        gitCommit: getGitCommit(),
        buildTime: getBuildTime(),
        buildTimestamp: getBuildTimestamp()
    };
}
function getBugsnagMetadata() {
    return {
        // The key `detect` is already used by bugsnag by default and is an existing tab in the bugsnag UI.
        // The key `deviceDetect` is used to disambiguate from the default information.
        deviceDetect: getDeviceDetectInfo(),
        build: getBuildInfo()
    };
}
/**
 * Decorates the provided bugsnag event object with current metadata.
 */
function onBugsnagError(event) {
    event.addMetadata('misc', { 'Page Visibility': document.visibilityState });
    const { sceneType, panelType, lastRoomEnterTimestampMs } = BUGSNAG_SESSION_STATE;
    if (sceneType) {
        event.addMetadata('misc', { Scene: sceneType });
    }
    if (panelType) {
        event.addMetadata('misc', { Panel: panelType });
    }
    if (lastRoomEnterTimestampMs !== undefined) {
        event.addMetadata('misc', {
            'Time Since Enter': `${Math.floor(performance.now() - lastRoomEnterTimestampMs).toLocaleString('en-US')}ms`
        });
    }
    const fpsStats = getFpsStats();
    if (fpsStats) {
        event.addMetadata('misc', { 'Average FPS': fpsStats.averageFps });
        event.addMetadata('misc', { 'FPS Samples': fpsStats.fpsSamples });
        event.addMetadata('misc', { 'Num FPS Samples': fpsStats.fpsSamples.length });
    }
}
/**
 * @returns the BugsnagErrorBoundary to wrap the React tree with
 */
export function startBugsnag() {
    const bugsnagMetadata = getBugsnagMetadata();
    if (isLocalhostEnvironment()) {
        console.log({ bugsnagMetadata });
    }
    Bugsnag.start({
        apiKey: API_KEY,
        context: getBugsnagContext(),
        enabledReleaseStages: ['production'],
        plugins: [new BugsnagPluginReact()],
        metadata: bugsnagMetadata,
        onError: onBugsnagError
    });
    return Bugsnag.getPlugin('react')?.createErrorBoundary(React);
}
/**
 * Asynchronously determine and set some bugsnag metadata without blocking anything.
 */
export async function addAsyncBugsnagMetadata() {
    const { isPrivate } = await detectIncognito();
    Bugsnag.addMetadata('deviceDetect', { 'Is Private Browser': isPrivate });
}
